import { useTheme } from '@mui/material/styles';
import { BaseHighchart } from './BaseHighchart';

/*
 * SunburstChart
 * --------------
 * Adds support for per-node colors.
 * Expects data points like:
 *   { id, parent, name, value?, color? }
 */

export const SunburstChart = ({
  rootName,
  data,
  title,
  drillable = true,
  dataLabels = true,
  colorByPoint = true,
  height = '100%',
  palette, // optional array of fallback colors
}) => {
  const theme = useTheme();
  const minArcPx = 32;
  const labelCharWidthPx = 7; // average per-character width at 14px font

  if (!data || !data.length) return null;

  const fallbackPalette =
    palette && palette.length
      ? palette
      : [
          theme.palette.primary.main,
          theme.palette.secondary.main,
          theme.palette.error.main,
          theme.palette.warning.main,
          theme.palette.info.main,
          theme.palette.success.main,
        ];

  const hashString = (value) => {
    if (!value) return 0;
    let hash = 0;
    for (let i = 0; i < value.length; i += 1) {
      hash = (hash << 5) - hash + value.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash);
  };

  const adjustHexColor = (hex, percent) => {
    if (!hex || hex[0] !== '#') return hex;
    const normalized = hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex;
    const num = parseInt(normalized.slice(1), 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + Math.round(2.55 * percent)));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + Math.round(2.55 * percent)));
    const b = Math.min(255, Math.max(0, (num & 0x0000ff) + Math.round(2.55 * percent)));
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  const arcLengthPx = (point) => {
    const shape = point?.shapeArgs || point?.node?.shapeArgs;
    if (!shape) return null;
    const { innerR, r, start, end } = shape;
    if ([innerR, r, start, end].some((val) => typeof val !== 'number')) return null;
    const midRadius = (innerR + r) / 2;
    return Math.max(0, (end - start) * midRadius);
  };

  const parentToChildren = new Map();
  data.forEach((node) => {
    const parentKey = node.parent ?? '';
    if (!parentToChildren.has(parentKey)) parentToChildren.set(parentKey, []);
    parentToChildren.get(parentKey).push(node);
  });

  const coloredData = data.map((node) => {
    const parentKey = node.parent ?? '';
    const isTopLevel = parentKey === '' || parentKey === 'ROOT' || parentKey == null;
    const colorKey = isTopLevel ? String(node.id) : String(parentKey);
    const parentColorIndex = hashString(colorKey) % fallbackPalette.length;
    const baseColor = fallbackPalette[parentColorIndex];

    const siblings = parentToChildren.get(parentKey) || [];
    const siblingIndex = Math.max(0, siblings.findIndex((s) => s.id === node.id));
    const shadeSpread = Math.max(1, siblings.length - 1);
    const shadePercent = 18 - Math.round((36 * siblingIndex) / shadeSpread);
    const derivedColor = adjustHexColor(baseColor, shadePercent);

    return {
      ...node,
      color: node.color || derivedColor || baseColor,
    };
  });

  const chartSpacing = [20, 40, 20, 40];

  const options = {
    chart: {
      height,
      backgroundColor: 'transparent',
      spacing: chartSpacing,
      style: { fontFamily: theme.typography.fontFamily },
    },
    title: {
      text: title || null,
      style: {
        color: theme.palette.text.primary,
        fontWeight: 500,
        fontSize: '1rem',
      },
    },
    credits: { enabled: false },
    tooltip: {
      headerFormat: '',
      pointFormat: `<b>{point.name}</b>: {point.value}`,
      borderColor: theme.palette.divider,
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
      style: { color: theme.palette.text.primary },
    },
    series: [
      {
        name: rootName,
        type: 'sunburst',
        innerSize: '60%', // larger visible ring area
        data: coloredData, // apply per-node color
        allowDrillToNode: drillable,
        cursor: drillable ? 'pointer' : 'default',
        borderColor: theme.palette.divider,
        borderWidth: 1,
        colorByPoint,
        dataLabels: { // 👇 global label style
          enabled: dataLabels,
          rotationMode: 'parallel',
          distance: 18,
          crop: false,
          overflow: 'allow',
          allowOverlap: false,
          textOverflow: 'none',
          textPath: { enabled: false },
          formatter() {
            const level = this.point?.node?.level;
            if (level && level > 1) return '';
            const name = this.point?.name || this.key || '';
            const arcPx = arcLengthPx(this.point);
            const text = String(name);
            if (arcPx == null) return text;
            if (arcPx < minArcPx || arcPx < text.length * labelCharWidthPx) return '';
            return text;
          },
          style: {
            fontFamily: theme.typography.fontFamily,
            fontSize: '14px',
            fontWeight: 500,       // optional: make it bolder
            color: theme.palette.text.primary,
            textOutline: 'none',
            textOverflow: 'none',
          },
        },
        levels: [
          {
            level: 1,
            levelIsConstant: false,
            dataLabels: {
              enabled: dataLabels,
              rotationMode: 'auto',
              distance: 20,
              crop: false,
              overflow: 'allow',
              allowOverlap: false,
              textOverflow: 'none',
              textPath: { enabled: false },
              formatter() {
                const level = this.point?.node?.level;
                if (level && level > 1) return '';
                const name = this.point?.name || this.key || '';
                const arcPx = arcLengthPx(this.point);
                const text = String(name);
                if (arcPx == null) return text;
                if (arcPx < minArcPx || arcPx < text.length * labelCharWidthPx) return '';
                return text;
              },
              style: {
                textOutline: 'none',
                color: theme.palette.text.primary,
                textOverflow: 'none',
              },
            },
            colorByPoint,
          },
          {
            level: 2,
            colorVariation: { key: 'brightness', to: -0.2 },
          },
          {
            level: 3,
            colorVariation: { key: 'brightness', to: 0.4 },
          },
        ]
      },
    ],
    plotOptions: {
      series: {
        animation: { duration: 400 },
        shadow: {
          color: 'rgba(0,0,0,0.1)',
          width: 3,
          offsetX: 1,
          offsetY: 2,
        },
        borderRadius: 2,
      },
    },
  };

  return <BaseHighchart options={options} />;
};
